import React, { forwardRef, useEffect, useRef, useState } from "react";
import { ConnectionQuality } from "@heygen/streaming-avatar";
import { useConnectionQuality } from "../logic/useConnectionQuality";
import { useStreamingAvatarSession } from "../logic/useStreamingAvatarSession";
import { StreamingAvatarSessionState } from "../logic";
import CloseIcon from "@mui/icons-material/Close";
import DefaultButton from "../../simple/DefaultButton";
import { Box, Typography, CircularProgress } from "@mui/material";

import { useTheme } from "@mui/material/styles";

export const AvatarVideo = forwardRef<HTMLVideoElement>((props, ref) => {
  const { sessionState, stream, stopAvatar } = useStreamingAvatarSession();
  const { connectionQuality } = useConnectionQuality();
  const videoRef =
    (ref as React.RefObject<HTMLVideoElement>) ||
    useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const { endSession } = props;

    const theme = useTheme();

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !stream) return;

    videoEl.srcObject = stream;

    // Start muted to satisfy autoplay policies
    videoEl.muted = true;

    const playVideo = async () => {
      try {
        await videoEl.play();
        setIsPlaying(true);
        console.log("Video playback started ✅");
        videoEl.muted = false;
      } catch (err) {
        console.warn("Autoplay failed, retrying…", err);
        videoEl.muted = true;
        try {
          await videoEl.play();
          setIsPlaying(true);
          videoEl.muted = false;
        } catch (err2) {
          console.error("Retrying video playback failed ❌", err2);
        }
      }
    };

    if (videoEl.readyState >= 2) {
      playVideo();
    } else {
      videoEl.onloadedmetadata = playVideo;
    }

    return () => {
      videoEl.onloadedmetadata = null;
    };
  }, [stream]);

  const isLoaded =
    sessionState === StreamingAvatarSessionState.CONNECTED && isPlaying;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "70vh", // adjust height to take most of the viewport
        maxHeight: "800px", // optional max height
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgb(35, 31, 32)",
        borderRadius: "12px",
        [theme.breakpoints.down("md")]: {
          height: "calc(100vh - 70px)",
          maxHeight: "none",
          borderRadius: 0
        }
      }}
    >
      {!isLoaded && (
        <div className="loader">
          <CircularProgress size={60} sx={{ color: "#ffffff" }} />
          <Typography variant="h5" mt={3} color="white">
            Loading video...
          </Typography>
        </div>
      )}

      {isLoaded && (
        <DefaultButton
          id="stop-session-button"
          onClick={endSession}
          sx={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
        >
          <CloseIcon /> Close session
        </DefaultButton>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted // ensures autoplay works in most browsers
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      >
        <track kind="captions" />
      </video>
    </Box>
  );
});

AvatarVideo.displayName = "AvatarVideo";
